// Behaviour that only matters inside the installed app (display-mode standalone):
// links to other sites open in the browser, links inside the app stay in its window,
// and a window opened straight on an inner screen still has the inbox to go "back" to.
import { isStandalone } from './installPrompt';

/** Paths the v2 router owns. Anything else on this origin (files, /static, /api) is left alone. */
const APP_PATH = /^\/(?:$|mail(?:\/|$)|compose$|search$|contacts$|money$|subscriptions$|outbox$|settings(?:\/|$)|rules$|cleanup$|trust$)/;

export function isAppPath(pathname: string): boolean {
  return APP_PATH.test(pathname);
}

export type LinkDecision =
  | { kind: 'ignore' }
  | { kind: 'external'; href: string }
  | { kind: 'internal'; path: string };

/**
 * Decides what a click on `anchor` should do in the installed app.
 * Pure apart from reading the anchor, so it can be unit tested.
 */
export function decideLink(
  anchor: HTMLAnchorElement,
  event: Pick<MouseEvent, 'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'defaultPrevented'>,
  origin: string,
): LinkDecision {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return { kind: 'ignore' };
  if (anchor.hasAttribute('download')) return { kind: 'ignore' };
  const raw = anchor.getAttribute('href');
  if (!raw || raw.startsWith('#')) return { kind: 'ignore' };
  let url: URL;
  try {
    url = new URL(anchor.href, origin);
  } catch {
    return { kind: 'ignore' };
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return { kind: 'ignore' }; // mailto:, tel:, blob: keep their own handling
  const target = (anchor.getAttribute('target') || '').toLowerCase();
  if (url.origin !== origin) {
    // Already opens outside (target=_blank); only a same-window link would take the app away.
    if (target === '_blank') return { kind: 'ignore' };
    return { kind: 'external', href: url.href };
  }
  if (target && target !== '_self') return { kind: 'ignore' };
  if (!isAppPath(url.pathname)) return { kind: 'ignore' };
  return { kind: 'internal', path: `${url.pathname}${url.search}${url.hash}` };
}

/**
 * Installs the click handler. `navigate` routes inside the app without a page load.
 * Returns a cleanup function. Does nothing outside the installed app.
 */
export function installStandaloneLinks(navigate: (path: string) => void): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const onClick = (event: MouseEvent) => {
    if (!isStandalone()) return;
    const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!anchor) return;
    const decision = decideLink(anchor, event, window.location.origin);
    if (decision.kind === 'external') {
      event.preventDefault();
      window.open(decision.href, '_blank', 'noopener,noreferrer');
    } else if (decision.kind === 'internal') {
      event.preventDefault();
      navigate(decision.path);
    }
  };
  // Bubble phase: router <Link>s handle their own clicks first and set defaultPrevented.
  document.addEventListener('click', onClick);
  return () => document.removeEventListener('click', onClick);
}

/** Screens a fresh window may open on where "back" should land on the inbox rather than close the app. */
const ROOT_PATHS = new Set(['/mail/inbox', '/']);

/**
 * Runs once, before the router starts. When the installed app opens straight on an inner
 * screen (a shortcut, a shared item, a mailto: link, a notification), the window's history
 * has only that one entry and the back gesture would close the app. Put the inbox underneath.
 * The entry shapes match React Router's ({ usr, key, idx }).
 */
export function seedStandaloneHistory(): boolean {
  if (typeof window === 'undefined' || !isStandalone()) return false;
  try {
    if (window.history.length > 1) return false;
    const { pathname, search, hash } = window.location;
    if (ROOT_PATHS.has(pathname) || !isAppPath(pathname)) return false;
    const state = window.history.state as { usr?: unknown } | null;
    window.history.replaceState({ usr: null, key: 'pwa-root', idx: 0 }, '', '/mail/inbox');
    window.history.pushState({ usr: state?.usr ?? null, key: 'pwa-start', idx: 1 }, '', `${pathname}${search}${hash}`);
    return true;
  } catch {
    return false;
  }
}

/** Minimal type for the Launch Handler API (launch_handler: focus-existing in the manifest). */
interface LaunchParams { targetURL?: string }
interface LaunchQueue { setConsumer: (consumer: (params: LaunchParams) => void) => void }

/**
 * With launch_handler "focus-existing", opening a shortcut, a shared item or a mailto: link while
 * the app is already open focuses that window and hands the URL here instead of reloading it.
 */
export function consumeLaunches(navigate: (path: string) => void): void {
  const queue = (window as Window & { launchQueue?: LaunchQueue }).launchQueue;
  if (!queue || typeof queue.setConsumer !== 'function') return;
  try {
    queue.setConsumer((params) => {
      if (!params?.targetURL) return;
      try {
        const url = new URL(params.targetURL);
        if (url.origin !== window.location.origin || !isAppPath(url.pathname)) return;
        const path = `${url.pathname}${url.search}${url.hash}`;
        const here = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (path !== here) navigate(path);
      } catch {
        // Not a URL we can route; ignore.
      }
    });
  } catch {
    // Launch handling is optional.
  }
}
